// src/components/profile/NewMember.jsx
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import RegionProvinceDistrictFields from '../common/RegionSelectFields';
import { regionsData } from '../../constants/regiones';
import ImageUploaderWithCrop from '../images/ImageUploaderWithCrop';
import Swal from 'sweetalert2';
import { countCompanies, createCompany } from '../../services/companiesService';
import { useAuth } from '../../context/authContext';
import { industries, periodicities, statusCompanies } from '../../constants/options';
import { Banner } from '../common/Banner';
// Modal para autocompletar desde postulaciones aceptadas
import AcceptedAppsPickerModal from '../membership/AcceptedAppsPickerModal';
// ✅ Marca en backend que usamos autocompletar sobre una postulación + guarda local
import { markApplicationAutofill, recordAutofillLocally } from '../../services/membershipApplications';

const inputClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 outline-none shadow-sm";
const selectClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 appearance-none bg-white shadow-sm";
const labelClass = "block mb-2 font-medium text-gray-700";
const errorClass = "text-sm text-red-500 mt-1 font-medium";
const helperClass = "text-xs text-gray-500 mt-1";
const sectionClass = "p-6 bg-white rounded-xl shadow-lg border border-gray-200 transition-all duration-300 hover:shadow-xl mb-8";

// Valores iniciales
const initialValues = {
  // ⬇️ NUEVO: para saber si el usuario autocompletó desde una postulación
  sourceApplicationId: null,

  rut: '',
  password: '',
  business_name: '',
  trade_name: '',
  industry: '',
  role: '',
  logo: null,
  status: '',
  company_size: '',
  entry_date: '',
  description: '',
  contact_email: '',
  web_page: '',
  landline_phone: '',
  mobile_phone: '',
  address: '',
  region: '',
  province: '',
  district: '',
  latitude: '',
  longitude: '',
  amount: '',
  periodicity: '',
  contact_name: '',
  email: '',
  phone_number: '',
  title_job: '',
  area: ''
};

// Validación
const validationSchema = Yup.object({
  rut: Yup.string().required('RUT es obligatorio').matches(/^\d{1,8}-[\dkK]$/, 'Formato de RUT inválido'),
  password: Yup.string().required('Contraseña es obligatoria'),
  business_name: Yup.string().required('Razón Social es obligatoria'),
  trade_name: Yup.string().required('Nombre Comercial es obligatorio'),
  industry: Yup.string().required('Industria es obligatoria'),
  role: Yup.string().required('Rol es obligatorio'),
  status: Yup.string().required('Estado es obligatorio'),
  company_size: Yup.string().required('Tamaño de la organización es obligatorio'),
  entry_date: Yup.string().required('Fecha de ingreso es obligatoria'),
  description: Yup.string().required('Descripción es obligatoria'),
  contact_email: Yup.string().email('Correo inválido').required('Correo de contacto es obligatorio'),
  web_page: Yup.string().url('URL inválida').required('Página web es obligatoria'),
  landline_phone: Yup.string().required('Teléfono fijo es obligatorio'),
  mobile_phone: Yup.string().required('Teléfono móvil es obligatorio'),
  address: Yup.string().required('Dirección es obligatoria'),
  region: Yup.string().required('Región es obligatoria'),
  province: Yup.string().required('Provincia es obligatoria'),
  district: Yup.string().required('Comuna es obligatoria'),
  latitude: Yup.number()
    .typeError('Debe ser un número')
    .min(-90, 'Mínimo -90')
    .max(90, 'Máximo 90'),
  longitude: Yup.number()
    .typeError('Debe ser un número')
    .min(-180, 'Mínimo -180')
    .max(180, 'Máximo 180'),
  amount: Yup.string()
    .required('Monto de la cuota es obligatorio')
    .test('is-decimal', 'Debe ser un número válido', v => !!v && /^(\d+(\.\d{1,2})?)$/.test(v)),
  periodicity: Yup.string().required('Periodicidad es obligatoria'),
  contact_name: Yup.string().required('Nombre completo es obligatorio'),
  email: Yup.string().email('Correo inválido').required('Correo electrónico es obligatorio'),
  phone_number: Yup.string().required('Teléfono es obligatorio'),
  title_job: Yup.string().required('Cargo es obligatorio'),
  area: Yup.string().required('Área es obligatoria'),
  logo: Yup.mixed()
    .required('La imagen es obligatoria')
    .test('fileSize','El archivo es demasiado grande. Máximo 2MB.', v => !!v && v.size <= 2 * 1024 * 1024)
});

// Utilidades de formato
const normalizeRut = (v) => {
  if (!v) return '';
  const d = String(v).replace(/\./g, '').replace(/[^0-9kK-]/g, '');
  const digits = d.replace(/[^0-9kK]/g, '');
  if (digits.length <= 1) return digits;
  return `${digits.slice(0, -1)}-${digits.slice(-1)}`;
};
const formatters = {
  rut: (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\./g, '').replace(/\D/g, '');
    if (cleaned.length <= 1) return cleaned;
    const dv = cleaned.slice(-1);
    const rutBody = cleaned.slice(0, -1);
    return `${rutBody}-${dv}`;
  },
  number: (value) => value ? value.replace(/,/g, '.').replace(/[^\d.]/g, '') : '',
};

// Input con formateador
function FormattedInput({ name, label, formatter, placeholder, helper, prefix }) {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const inputRef = useRef(null);

  const handleChange = (e) => setFieldValue(name, formatter(e.target.value));
  const handleClick = () => {
    if (inputRef.current && prefix) {
      const pLen = prefix.length;
      const cursorPos = Math.max(pLen, inputRef.current.selectionStart);
      inputRef.current.setSelectionRange(cursorPos, cursorPos);
    }
  };

  return (
    <div className="mb-4">
      <label htmlFor={name} className={labelClass}>{label} <span className="text-red-500">*</span></label>
      <input
        ref={inputRef}
        id={name}
        name={name}
        value={values[name]}
        onChange={handleChange}
        onClick={handleClick}
        placeholder={placeholder}
        className={inputClass}
      />
      {helper && <p className={helperClass}>{helper}</p>}
      {touched[name] && errors[name] && <div className={errorClass}>{errors[name]}</div>}
    </div>
  );
}

function TextField({ name, label, type = 'text', as = 'input', placeholder, helper }) {
  return (
    <div className="mb-4">
      <label htmlFor={name} className={labelClass}>{label} <span className="text-red-500">*</span></label>
      <Field id={name} name={name} type={type} as={as} placeholder={placeholder} className={inputClass} />
      {helper && <p className={helperClass}>{helper}</p>}
      <ErrorMessage name={name} component="div" className={errorClass} />
    </div>
  );
}

function SelectField({ name, label, options }) {
  return (
    <div className="mb-4">
      <label htmlFor={name} className={labelClass}>{label} <span className="text-red-500">*</span></label>
      <Field as="select" id={name} name={name} className={selectClass}>
        <option value="">Seleccione...</option>
        {options.map((opt) =>
          typeof opt === 'object' && opt !== null
            ? <option key={opt.value} value={opt.value}>{opt.label}</option>
            : <option key={opt} value={opt}>{opt}</option>
        )}
      </Field>
      <ErrorMessage name={name} component="div" className={errorClass} />
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="flex items-center mb-6 pb-2 border-b border-gray-200">
      <div className="bg-indigo-100 p-2 rounded-lg mr-3">
        <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
    </div>
  );
}

export default function FormularioCreacion({ isSubmitting, onUpdate }) {
  const { user } = useAuth();
  const [imagePreview, setImagePreview] = useState(null);
  const [counter, setCounter] = useState(0);
  const [openPicker, setOpenPicker] = useState(false);
  const [autofillSource, setAutofillSource] = useState(null); // { id, razonSocial/nombreFantasia }

  const companySizes = [
    { label: "Micro (2 uf)", value: "Micro" },
    { label: "Pequeña (2,5 uf)", value: "Small" },
    { label: "Mediana (5 uf)", value: "Medium" },
    { label: "Grande (10 uf)", value: "Big" }
  ];
  const role = [{ label: 'Administrador', value:'admin' }, { label: 'Socio', value:'socio' }];

  useEffect(() => {
    (async () => {
      try {
        const response = await countCompanies(user);
        setCounter(response?.count || 0);
      } catch (error) {
        console.error("Error al obtener la cantidad de compañias:", error);
      }
    })();
  }, [user]);

  const handleSubmit = async (data) => {
    try {
      // Normalizo RUT por seguridad (venga de input o de autocompletar)
      const payload = { ...data, rut: normalizeRut(data?.rut || '') };

      const { isConfirmed } = await Swal.fire({
        title: 'Confirmar envío',
        text: "¿Está seguro de que desea crear este nuevo socio?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, crear',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      });
      if (!isConfirmed) return;

      isSubmitting?.(true);
      const response = await createCompany(payload, user); // el servicio enviará FormData si hay logo
      isSubmitting?.(false);

      if (!response?.success) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response?.message || 'Error al crear el socio. Intente nuevamente.',
          confirmButtonText: 'Aceptar'
        });
        return;
      }

      // ✅ SOLO AHORA marcamos como "Completada" si venía de una postulación
      if (payload.sourceApplicationId) {
        try {
          await markApplicationAutofill(payload.sourceApplicationId, {
            used: true,
            by: user?.email || user?.id || 'create-company',
          });
          // También refleja en sesión/local para el chip "Completada" del modal
          recordAutofillLocally(payload.sourceApplicationId);
        } catch (e) {
          console.warn('No se pudo marcar autofill:', e?.message || e);
        }
      }

      await Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: 'El socio ha sido creado exitosamente.',
        confirmButtonText: 'Aceptar'
      });
      onUpdate?.();
    } catch (error) {
      console.error("Error al crear socio:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'Error al crear el socio. Intente nuevamente.',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      isSubmitting?.(false);
    }
  };

  // Cuando el modal indique que se autocompletó desde una postulación
  const handleAutofillPicked = async (app) => {
    if (!app) return;
    // Priorizar razón comercial (nombreFantasia), luego razón social
    const razonComercial = app?.nombreFantasia || app?.razonSocial || app?.rutEmpresa || `ID ${app.id}`;
    setAutofillSource({
      id: app.id,
      name: razonComercial,
    });
    // ❌ Ya no marcamos aquí. Se marcará en handleSubmit si se crea el socio.
  };

  return (
    <div className="max-w-full mx-auto">
      <Banner title={'Crear Nuevo Socio'} message={'Complete los campos para crear un nuevo socio.'}/>
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-600">Cantidad de socios actuales: {counter}</p>
        <button
          type="button"
          onClick={() => setOpenPicker(true)}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow"
          title="Autocompletar con una postulación aceptada"
        >
          Autocompletar desde postulación aceptada
        </button>
      </div>

      {autofillSource && (
        <div className="mb-4 p-4 rounded-lg border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-800 shadow-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-emerald-600" size={20} />
            <p className="font-medium">
              Datos autocompletados desde la postulación de la empresa <b className="text-emerald-700">{autofillSource.name}</b>
            </p>
          </div>
        </div>
      )}

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ errors, touched, isValid, isSubmitting: formSubmitting, setFieldValue, setFieldTouched, setFieldError }) => (
          <Form className="space-y-6">
            {/* Información Básica */}
            <section className={sectionClass}>
              <SectionHeader title="Información Básica" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormattedInput name="rut" label="RUT" formatter={formatters.rut} placeholder="12345678-9" helper="Sin puntos y con guion" />
                <TextField name="password" label="Contraseña" type="password" />
                <TextField name="business_name" label="Razón Social" />
                <TextField name="trade_name" label="Nombre Comercial" />
                <SelectField name="industry" label="Industria" options={industries} />
                <SelectField name="role" label="Rol" options={role} />

                <div className="w-full md:w-1/2">
                  <ImageUploaderWithCrop
                    aspectRatio={1}
                    onCropped={(blob) => {
                      const url = URL.createObjectURL(blob);
                      setFieldValue("logo", blob);
                      setImagePreview(url);
                      setFieldTouched("logo", true, false);
                    }}
                    setFieldTouched={setFieldTouched}
                    setFieldError={setFieldError}
                    maxFileSize={2 * 1024 * 1024}
                    fieldName="logo"
                  />
                  <p className={helperClass}>Formatos: JPG, PNG. Tamaño máximo: 2MB</p>
                  {touched.logo && errors.logo && <p className="text-red-500 text-sm mt-1">{errors.logo}</p>}
                </div>
                <div className="w-full md:w-1/2 flex justify-center items-center border border-dashed border-gray-300 rounded-lg p-4 min-h-40">
                  {imagePreview
                    ? <img src={imagePreview} alt="Vista previa" className="max-h-48 object-contain" />
                    : <p className="text-gray-400 text-center text-sm">Vista previa de la imagen</p>}
                </div>

                <SelectField name="status" label="Estado" options={statusCompanies} />
                <SelectField name="company_size" label="Tamaño de la organización" options={companySizes} />
                <TextField name="entry_date" label="Fecha de ingreso" placeholder="dd-mm-yyyy" />
                <div className="col-span-1 md:col-span-2">
                  <TextField name="description" label="Descripción" as="textarea" placeholder="Ingrese una descripción para la organización" />
                </div>
              </div>
            </section>

            {/* Información de contacto */}
            <section className={sectionClass}>
              <SectionHeader title="Información de Contacto" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField name="contact_email" label="Correo de contacto" type="email" placeholder="ejemplo@organización.cl" />
                <TextField name="web_page" label="Página web" placeholder="https://empresa.cl" />
                <TextField name="landline_phone" label="Teléfono fijo" placeholder="(51)-2 12345" />
                <TextField name="mobile_phone" label="Teléfono móvil" placeholder="Ej: +56 9 12345678" />
              </div>
            </section>

            {/* Dirección */}
            <section className={sectionClass}>
              <SectionHeader title="Dirección" />
              <TextField name="address" label="Dirección" />
              <RegionProvinceDistrictFields
                regions={regionsData?.Regions || []}
                styles={{ selectClass, labelClass, errorClass }}
                showLabels={true}
                helperTexts={{
                  region: "Seleccione la región",
                  province: "Seleccione la provincia",
                  district: "Seleccione la comuna"
                }}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField name="latitude" label="Latitud" type="number" placeholder="-33.456" helper="Decimal, ej: -33.456" />
                <TextField name="longitude" label="Longitud" type="number" placeholder="-70.648" helper="Decimal, ej: -70.648" />
              </div>
            </section>

            {/* Información de membresía */}
            <section className={sectionClass}>
              <SectionHeader title="Información de Membresía" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormattedInput name="amount" label="Monto de la cuota" formatter={formatters.number} placeholder="Ej: 10.50" helper="Use punto como separador decimal" />
                <SelectField name="periodicity" label="Periodicidad" options={periodicities} />
              </div>
            </section>

            {/* Contacto de cobranza */}
            <section className={sectionClass}>
              <SectionHeader title="Contacto de cobranza" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField name="contact_name" label="Nombre completo" />
                <TextField name="email" label="Correo electrónico" type="email" />
                <TextField name="phone_number" label="Teléfono" />
                <TextField name="title_job" label="Cargo" />
                <TextField name="area" label="Área" />
              </div>
            </section>

            <button
              type="submit"
              disabled={!isValid || formSubmitting}
              className={`w-full p-4 rounded-lg font-medium shadow-md transition-all duration-300 
                ${!isValid || formSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#1BBB55] hover:from-[#1BBB55] hover:to-[#1aab51] text-white'}`}
            >
              {formSubmitting ? 'Procesando...' : 'Crear Socio'}
            </button>

            {!isValid && Object.keys(errors).length > 0 && (
              <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg">
                <h4 className="font-semibold text-red-700 mb-2">Faltan campos o hay errores:</h4>
                <ul className="list-disc list-inside text-red-600 text-sm">
                  {Object.entries(errors).map(([field, errorMsg]) => (<li key={field}>{errorMsg}</li>))}
                </ul>
              </div>
            )}

            {/* Modal de autocompletar */}
            <AcceptedAppsPickerModal
              open={openPicker}
              onClose={() => setOpenPicker(false)}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              setImagePreview={setImagePreview}
              // ⬇️ Además de mostrar el banner, guardamos el ID fuente en el form
              onPicked={(app) => {
                handleAutofillPicked(app);
                setFieldValue('sourceApplicationId', app?.id || null);
                setFieldTouched('sourceApplicationId', true, false);
              }}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
}
